import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ApplyTemplateInput,
  ArchiveSyncResponse,
  AuditAckDto,
  BalanceAuditResponse,
  CcyCode,
  ChangeExecutorInput,
  ChatBalanceSource,
  ChatBalancesConfigDto,
  ChatSyncConfigDto,
  ChatSyncOutcome,
  CreateAckInput,
  CreateCurrencyInput,
  CreateListeningChatInput,
  CreateOrderInput,
  CreateTronWalletInput,
  Currency,
  DeleteOrderInput,
  EditProvodkaInput,
  FindingDto,
  OrderDetail,
  RateSourceConfig,
  RefreshRateResult,
  RejectOrderInput,
  RuleAction,
  RuleGuard,
  TelegramBotConfigDto,
  TelegramEvent,
  TelegramListeningChat,
  TelegramRule,
  TestBotTokenInput,
  TestBotTokenResult,
  TestRegexInput,
  TestRegexResult,
  TronConfigDto,
  TronRefreshResult,
  TronWalletRow,
  UpdateChatBalancesConfigInput,
  UpdateChatSyncConfigInput,
  UpdateListeningChatInput,
  UpdateTelegramBotConfigInput,
  UpdateTronConfigInput,
  UpdateTronWalletInput,
  UpsertRuleInput,
  UsdtRate,
  AgentTaskAccepted,
  AgentTaskRequest,
} from '@wave/shared';
import { api } from './client';
import { queryKeys } from './hooks';

function invalidateLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['orders'] });
  qc.invalidateQueries({ queryKey: queryKeys.balances });
  qc.invalidateQueries({ queryKey: queryKeys.dashboard });
  qc.invalidateQueries({ queryKey: ['transactions'] });
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrderInput) => api.post<OrderDetail>('/orders', input),
    onSuccess: () => invalidateLists(qc),
  });
}

export function useRejectOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RejectOrderInput) =>
      api.post<OrderDetail>(`/orders/${orderId}/reject`, input),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.order(orderId), data);
      invalidateLists(qc);
    },
  });
}

export function useDeleteOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DeleteOrderInput) =>
      api.post<OrderDetail>(`/orders/${orderId}/delete`, input),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.order(orderId), data);
      invalidateLists(qc);
    },
  });
}

/** Сменить исполнителя заявки (с обязательной причиной → аудит). */
export function useChangeExecutor(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangeExecutorInput) =>
      api.patch<OrderDetail>(`/orders/${orderId}/executor`, input),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.order(orderId), data);
      qc.invalidateQueries({ queryKey: queryKeys.executorHistory(orderId) });
      invalidateLists(qc);
    },
  });
}

export function useConfirmProvodka(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idx: number) =>
      api.post<OrderDetail>(`/orders/${orderId}/provodki/${idx}/confirm`),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.order(orderId), data);
      invalidateLists(qc);
    },
  });
}

/** Откатить подтверждение ноги (done → pending) — только admin. */
export function useRollbackConfirm(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idx: number) =>
      api.post<OrderDetail>(`/orders/${orderId}/provodki/${idx}/rollback`),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.order(orderId), data);
      invalidateLists(qc);
    },
  });
}

/** Добавить валюту (код+символ+scale+позиция+стартовый курс к USDT). */
export function useCreateCurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCurrencyInput) => api.post<Currency>('/ref/currencies', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ref'] });
    },
  });
}

export function useUpdateUsdtRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { ccy: string; rate: string; autoManaged?: boolean }) =>
      api.put<UsdtRate>(`/ref/usdt-rates/${vars.ccy}`, {
        rate: vars.rate,
        ...(vars.autoManaged !== undefined ? { autoManaged: vars.autoManaged } : {}),
      }),
    onSuccess: () => invalidateRates(qc),
  });
}

function invalidateRates(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: queryKeys.usdtRates });
  qc.invalidateQueries({ queryKey: queryKeys.dashboard });
  qc.invalidateQueries({ queryKey: queryKeys.balances });
  qc.invalidateQueries({ queryKey: ['balances', 'timeseries'] });
}

/**
 * Лёгкий toggle авто-обновления курса. Отдельно от useUpdateUsdtRate:
 *  - PATCH /usdt-rates/:ccy/auto не ставит source='manual';
 *  - оптимистичный апдейт галочки (мгновенный отклик);
 *  - узкая инвалидация (только список курсов), без дашборда/балансов/графиков.
 *
 * Опции вынесены в фабрику, чтобы гонку «invalidate во время параллельного
 * toggle» можно было воспроизводить в тесте без React (mutations.test.ts).
 */
const TOGGLE_AUTO_MUTATION_KEY = ['usdt-rates', 'toggle-auto'] as const;

export function toggleAutoManagedOptions(qc: QueryClient) {
  return {
    mutationKey: TOGGLE_AUTO_MUTATION_KEY,
    mutationFn: (vars: { ccy: string; autoManaged: boolean }) =>
      api.patch<UsdtRate>(`/ref/usdt-rates/${vars.ccy}/auto`, { autoManaged: vars.autoManaged }),
    onMutate: async (vars: { ccy: string; autoManaged: boolean }) => {
      await qc.cancelQueries({ queryKey: queryKeys.usdtRates });
      const prev = qc.getQueryData<UsdtRate[]>(queryKeys.usdtRates);
      qc.setQueryData<UsdtRate[]>(queryKeys.usdtRates, (old) =>
        old?.map((r) => (r.ccy === vars.ccy ? { ...r, autoManaged: vars.autoManaged } : r)),
      );
      return { prev };
    },
    onError: (_e: unknown, _v: unknown, ctx?: { prev?: UsdtRate[] }) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.usdtRates, ctx.prev);
    },
    onSettled: () => {
      // Инвалидируем только когда settle'ится ПОСЛЕДНЯЯ pending-мутация:
      // иначе рефетч после первого клика вернёт серверное состояние до
      // применения второго и визуально откатит чекбокс (флакер).
      if (qc.isMutating({ mutationKey: TOGGLE_AUTO_MUTATION_KEY }) === 1) {
        qc.invalidateQueries({ queryKey: queryKeys.usdtRates });
      }
    },
  };
}

export function useToggleAutoManaged() {
  const qc = useQueryClient();
  return useMutation(toggleAutoManagedOptions(qc));
}

export function useUpdateRateSources(ccy: CcyCode) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sources: RateSourceConfig[]) =>
      api.put<RateSourceConfig[]>(`/ref/usdt-rates/${ccy}/sources`, { sources }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ref', 'usdt-rates', 'sources', ccy] });
    },
  });
}

export function useRefreshRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ccy: CcyCode) =>
      api.post<RefreshRateResult>(`/ref/usdt-rates/${ccy}/refresh`),
    onSuccess: (_data, ccy) => {
      invalidateRates(qc);
      qc.invalidateQueries({ queryKey: ['ref', 'usdt-rates', 'history', ccy] });
    },
  });
}

// ─── telegram-listener ───

function invalidateTelegram(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['telegram'] });
}

export function useUpsertTelegramChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateListeningChatInput) =>
      api.post<TelegramListeningChat>('/telegram/chats', input),
    onSuccess: () => invalidateTelegram(qc),
  });
}

export function useSyncTelegramChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ChatSyncOutcome>('/telegram/chats/sync'),
    // Синхрон пишет в reference.accounts → инвалидируем список аккаунтов («Чаты»).
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ref', 'accounts'] }),
  });
}

export function useSaveChatSyncConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateChatSyncConfigInput) =>
      api.put<ChatSyncConfigDto>('/telegram/chat-sync/config', input),
    onSuccess: () => invalidateTelegram(qc),
  });
}

/** Сохранить конфиг источника реальных остатков по чатам (URL/токен/enabled). */
export function useSaveChatBalancesConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateChatBalancesConfigInput) =>
      api.put<ChatBalancesConfigDto>('/chat-balances/config', input),
    // Меняем источник → инвалидируем и конфиг, и сам список остатков.
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.chatBalances }),
  });
}

/**
 * Кто хозяин остатков чата: 'auto' | 'api' | 'telegram'. Меняет картину не
 * только на экране — от этого зависят снапшоты истории, ингест журнала бота и
 * сверка, поэтому инвалидируем весь блок chat-balances (включая историю).
 */
export function useSetChatBalanceSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { chatId: string; source: ChatBalanceSource }) =>
      api.put<{ chatId: string; source: ChatBalanceSource }>(
        `/chat-balances/chat/${vars.chatId}/source`,
        { source: vars.source },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-balances'] }),
  });
}

export function useUpdateTelegramChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { chatId: string; patch: UpdateListeningChatInput }) =>
      api.patch<TelegramListeningChat>(`/telegram/chats/${vars.chatId}`, vars.patch),
    onSuccess: () => invalidateTelegram(qc),
  });
}

export function useDeleteTelegramChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chatId: string) => api.delete<{ chatId: string }>(`/telegram/chats/${chatId}`),
    onSuccess: () => invalidateTelegram(qc),
  });
}

export function useCreateTelegramRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertRuleInput) => api.post<TelegramRule>('/telegram/rules', input),
    onSuccess: () => invalidateTelegram(qc),
  });
}

export function useUpdateTelegramRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<UpsertRuleInput> }) =>
      api.patch<TelegramRule>(`/telegram/rules/${vars.id}`, vars.patch),
    onSuccess: () => invalidateTelegram(qc),
  });
}

export function useDeleteTelegramRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/telegram/rules/${id}`),
    onSuccess: () => invalidateTelegram(qc),
  });
}

export function useTestRegex() {
  return useMutation({
    mutationFn: (input: TestRegexInput) =>
      api.post<TestRegexResult>('/telegram/rules/test', input),
  });
}

// ─── dry-run правил (полная проверка без записи) ───

/**
 * Вход dry-run: правило целиком (не обязательно сохранённое).
 * Либо `sample` (проверка текста), либо `chatId`+`messageLimit`
 * (ретро-прогон по последним сообщениям message_log).
 */
export interface RuleDryRunInput {
  pattern: string;
  flags: string;
  actions: RuleAction[];
  guards: RuleGuard[];
  chatId?: string;
  sample?: string;
  messageLimit?: number;
}

/** Один шаг цепочки действий в dry-run: validate/preview, без побочных эффектов. */
export interface RuleDryRunChainStep {
  type: string;
  ok: boolean;
  preview?: unknown;
  error?: string;
}

export interface RuleDryRunMatch {
  groups: Record<string, string>;
  guardsOk: boolean;
  guardsReason: string | null;
  chain: RuleDryRunChainStep[];
}

export interface RuleDryRunResultItem {
  messageLogId: number | null;
  text: string;
  receivedAt: string | null;
  matches: RuleDryRunMatch[];
}

export interface RuleDryRunResult {
  results: RuleDryRunResultItem[];
}

/**
 * Полная проверка правила без записи: regex → guards → validate всех actions.
 * Контракт: POST /telegram/rules/dry-run. Типы объявлены локально (структурно
 * совместимы с серверными Zod-схемами) — при появлении shared-схем dry-run их
 * можно заменить импортом без изменения поведения.
 */
export function useDryRunRule() {
  return useMutation({
    mutationFn: (input: RuleDryRunInput) =>
      api.post<RuleDryRunResult>('/telegram/rules/dry-run', input),
  });
}

export function useApplyTelegramEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<TelegramEvent>(`/telegram/events/${id}/apply`),
    onSuccess: () => {
      invalidateTelegram(qc);
      // Apply update-rate / confirm-provodka меняет данные в других модулях.
      qc.invalidateQueries({ queryKey: ['ref', 'usdt-rates'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: queryKeys.balances });
      qc.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
  });
}

export function useDismissTelegramEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<TelegramEvent>(`/telegram/events/${id}/dismiss`),
    onSuccess: () => invalidateTelegram(qc),
  });
}

/** Сохранить конфиг бота (token, mode, enabled, webhook). */
export function useSaveBotConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTelegramBotConfigInput) =>
      api.put<TelegramBotConfigDto>('/telegram/bot/config', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['telegram', 'bot'] });
    },
  });
}

/** Проверить токен через getMe (без сохранения). */
export function useTestBotToken() {
  return useMutation({
    mutationFn: (input: TestBotTokenInput) =>
      api.post<TestBotTokenResult>('/telegram/bot/test', input),
  });
}

/** Создать правило из template. */
export function useCreateRuleFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplyTemplateInput) =>
      api.post<TelegramRule>('/telegram/rules/from-template', input),
    onSuccess: () => invalidateTelegram(qc),
  });
}

/** Уточнить фикс курсов профита заявки (до закрытия). */
export function useUpdateProfitRates(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rates: Record<string, string>) =>
      api.patch<OrderDetail>(`/orders/${orderId}/profit-rates`, { rates }),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.order(orderId), data);
      invalidateLists(qc);
    },
  });
}

export function useEditProvodka(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { idx: number; patch: EditProvodkaInput }) =>
      api.patch<OrderDetail>(`/orders/${orderId}/provodki/${vars.idx}`, vars.patch),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.order(orderId), data);
      invalidateLists(qc);
    },
  });
}

/** Ручной прогон сверки-снапшота (кнопка «⟳ Сверить сейчас»). */
export function useRunAuditSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<BalanceAuditResponse>('/balance-audit/snapshot'),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.balanceAudit, data);
      qc.invalidateQueries({ queryKey: ['balance-audit', 'history'] });
    },
  });
}

/** Пометить дельту объяснённой. */
export function useCreateAuditAck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAckInput) => api.post<AuditAckDto>('/balance-audit/acks', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.balanceAudit }),
  });
}

/** Снять отметку «объяснено». */
export function useReleaseAuditAck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ackId: number) => api.delete<{ released: boolean }>(`/balance-audit/acks/${ackId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.balanceAudit }),
  });
}

// ─── archive audit (вкладка «Целостность») ───

function invalidateArchive(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: queryKeys.archiveFindings });
  qc.invalidateQueries({ queryKey: queryKeys.archiveCoverage });
}

/** Закрыть находку с комментарием (право audit.ack). */
export function useResolveFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; comment: string }) =>
      api.post<FindingDto>(`/archive/findings/${vars.id}/resolve`, { comment: vars.comment }),
    onSuccess: () => invalidateArchive(qc),
  });
}

/** Вернуть закрытую находку в open (право audit.ack). */
export function useReopenFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<FindingDto>(`/archive/findings/${id}/reopen`),
    onSuccess: () => invalidateArchive(qc),
  });
}

/** Ручной прогон проверок целостности архива (кнопка «⟳ Проверить сейчас»). */
export function useRunArchiveSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ArchiveSyncResponse>('/archive/sync'),
    onSuccess: () => invalidateArchive(qc),
  });
}

// ─── tron-wallets ───

/** Меняем ключ/кошельки/enabled → инвалидируем конфиг+список кошельков и остатки (страница /balances подмешивает эти строки). */
function invalidateTron(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: queryKeys.tronConfig });
  qc.invalidateQueries({ queryKey: queryKeys.tronWallets });
  qc.invalidateQueries({ queryKey: queryKeys.chatBalances });
}

/** Сохранить конфиг опроса TronScan (apiKey: строка → перешифровать; null → очистить; отсутствует → не менять). */
export function useUpdateTronConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTronConfigInput) => api.put<TronConfigDto>('/tron-wallets/config', input),
    onSuccess: () => invalidateTron(qc),
  });
}

/** Добавить отслеживаемый TRON-кошелёк (409 при дубле адреса — см. routes.ts). */
export function useCreateTronWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTronWalletInput) => api.post<TronWalletRow>('/tron-wallets', input),
    onSuccess: () => invalidateTron(qc),
  });
}

/** Изменить label/enabled кошелька. */
export function useUpdateTronWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { address: string; patch: UpdateTronWalletInput }) =>
      api.patch<TronWalletRow>(`/tron-wallets/${vars.address}`, vars.patch),
    onSuccess: () => invalidateTron(qc),
  });
}

export function useDeleteTronWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (address: string) => api.delete<{ ok: true }>(`/tron-wallets/${address}`),
    onSuccess: () => invalidateTron(qc),
  });
}

/** Ручной опрос всех enabled-кошельков через TronScan прямо сейчас (кнопка «⟳ Обновить сейчас»). */
export function useRefreshTronWallets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<TronRefreshResult>('/tron-wallets/refresh'),
    onSuccess: () => invalidateTron(qc),
  });
}

/** Поставить агенту-аудитору разбор (ответ придёт в чат алертов). Право audit.view. */
export function useAskAgent() {
  return useMutation({
    mutationFn: (req: AgentTaskRequest) => api.post<AgentTaskAccepted>('/agent-tasks', req),
  });
}
